import type { FixedIdAssetState } from './fixed-id-assets.dto.js';
import type { HoldingUnitEventEnvelopeBase } from './holding-unit-event-envelope.dto.js';
import type { HoldingUnitContainerKind, HoldingUnitIdentityMode } from './holding-units.dto.js';

export const fixedIdAssetEventTypeValues = [
  'fixedIdAssetRegistered',
  'fixedIdAssetMasterTareUpdated',
  'fixedIdAssetStateChanged',
  'fixedIdAssetLocationUpdated',
] as const;
export type FixedIdAssetEventType = (typeof fixedIdAssetEventTypeValues)[number];

export interface FixedIdAssetRegisteredEventPayload {
  identityMode: Extract<HoldingUnitIdentityMode, 'fixed_asset_cycle'>;
  containerKind: Extract<HoldingUnitContainerKind, 'heavy_container'>;
  physicalId: string;
  normalizedPhysicalId: string;
  normalizedScannableIdentifier: string;
  masterTareLb: number;
  state: Extract<FixedIdAssetState, 'available'>;
  homeStoreId: string;
  homeLocationId: string;
  lastKnownStoreId: string;
  lastKnownLocationId: string;
  lastKnownYardLocationId: string | null;
}

export interface FixedIdAssetMasterTareUpdatedEventPayload {
  previousMasterTareLb: number;
  masterTareLb: number;
}

export interface FixedIdAssetStateChangedEventPayload {
  fromState: FixedIdAssetState;
  toState: FixedIdAssetState;
  activeCycleUnitId?: string | null;
  reason?: string | null;
}

export interface FixedIdAssetLocationUpdatedEventPayload {
  lastKnownStoreId: string;
  lastKnownLocationId: string;
  lastKnownYardLocationId: string | null;
}

export interface FixedIdAssetEventBase
  extends Omit<HoldingUnitEventEnvelopeBase, 'entityType' | 'eventType' | 'payload'> {
  entityType: 'fixedIdAsset';
  eventType: FixedIdAssetEventType;
}

export interface FixedIdAssetRegisteredEvent extends FixedIdAssetEventBase {
  eventType: 'fixedIdAssetRegistered';
  payload: FixedIdAssetRegisteredEventPayload;
}

export interface FixedIdAssetMasterTareUpdatedEvent extends FixedIdAssetEventBase {
  eventType: 'fixedIdAssetMasterTareUpdated';
  payload: FixedIdAssetMasterTareUpdatedEventPayload;
}

export interface FixedIdAssetStateChangedEvent extends FixedIdAssetEventBase {
  eventType: 'fixedIdAssetStateChanged';
  payload: FixedIdAssetStateChangedEventPayload;
}

export interface FixedIdAssetLocationUpdatedEvent extends FixedIdAssetEventBase {
  eventType: 'fixedIdAssetLocationUpdated';
  payload: FixedIdAssetLocationUpdatedEventPayload;
}

export type FixedIdAssetEvent =
  | FixedIdAssetRegisteredEvent
  | FixedIdAssetMasterTareUpdatedEvent
  | FixedIdAssetStateChangedEvent
  | FixedIdAssetLocationUpdatedEvent;
